import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'src/utils/shadcn';

const generalBlockVariants = cva('shadow-xs group relative flex flex-col gap-4 rounded-xl border p-4', {
    variants: {
        variant: {
            default: 'border-gray-200 bg-white',
            disabled: 'bg-gray-50 text-gray-500',
            error: 'border-red-200 bg-red-50',
            warn: 'border-orange-200 bg-orange-50',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

interface GeneralBlockProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof generalBlockVariants> {
    asChild?: boolean;
}
function GeneralBlock({ className, variant, children, asChild, ...props }: GeneralBlockProps) {
    const Component = asChild ? Slot : 'div';
    return (
        <Component className={cn(generalBlockVariants({ variant, className }))} {...props}>
            {children}
        </Component>
    );
}

const generalBlockHeaderVariants = cva('flex gap-4 text-gray-600', {
    variants: {
        variant: {
            default: 'items-center',
            dashboard: 'items-start justify-between',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});
interface GeneralBlockHeaderProps
    extends React.HTMLAttributes<HTMLDivElement>,
        VariantProps<typeof generalBlockHeaderVariants> {}
function GeneralBlockHeader({ className, children, variant }: GeneralBlockHeaderProps) {
    return <div className={cn(generalBlockHeaderVariants({ variant, className }))}>{children}</div>;
}

interface GeneralBlockHeadProps extends React.HTMLAttributes<HTMLDivElement> {}
function GeneralBlockHead({ className, children }: GeneralBlockHeadProps) {
    return <div className={cn('flex flex-col', className)}>{children}</div>;
}

interface GeneralBlockTitleProps extends React.HTMLAttributes<HTMLParagraphElement> {}
function GeneralBlockTitle({ className, children }: GeneralBlockTitleProps) {
    return <p className={cn('text-sm font-semibold text-gray-500', className)}>{children}</p>;
}

interface GeneralBlockSubtitleProps extends React.HTMLAttributes<HTMLParagraphElement> {}
function GeneralBlockSubtitle({ className, children }: GeneralBlockSubtitleProps) {
    return <p className={cn('text-sm text-gray-500', className)}>{children}</p>;
}

const generalBlockBodyVariants = cva('flex flex-wrap justify-between', {
    variants: {
        variant: {
            default: '-mt-2 items-end gap-2',
            dashboard: 'h-full flex-col gap-4 lg:flex-row',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

interface GeneralBlockBodyProps
    extends React.HTMLAttributes<HTMLDivElement>,
        VariantProps<typeof generalBlockBodyVariants> {}
function GeneralBlockBody({ className, children, variant }: GeneralBlockBodyProps) {
    return <div className={cn(generalBlockBodyVariants({ variant, className }))}>{children}</div>;
}

const generalBlockValueVariants = cva('grow-9999 text-2xl font-semibold sm:text-3xl md:text-4xl', {
    variants: {
        size: {
            default: 'text-2xl sm:text-3xl md:text-4xl',
            small: 'text-base sm:text-base text-gray-900 md:text-base font-normal',
        },
    },
    defaultVariants: {
        size: 'default',
    },
});

interface GeneralBlockValueProps
    extends React.HTMLAttributes<HTMLHeadingElement>,
        VariantProps<typeof generalBlockValueVariants> {}
function GeneralBlockValue({ className, children, size, ...props }: GeneralBlockValueProps) {
    return (
        <p className={cn(generalBlockValueVariants({ size, className }))} {...props}>
            {children ?? '-'}
        </p>
    );
}

interface GeneralBlockLabelProps extends React.HTMLAttributes<HTMLDivElement> {}
function GeneralBlockLabel({ className, children }: GeneralBlockLabelProps) {
    return (
        <div className={cn('flex shrink-0 items-center gap-2 text-right text-sm font-medium text-gray-500', className)}>
            {children}
        </div>
    );
}

interface GeneralBlockSeparatorProps extends React.HTMLAttributes<HTMLHRElement> {}
function GeneralBlockSeparator({ className, ...props }: GeneralBlockSeparatorProps) {
    return (
        <hr
            className={cn(
                '-mx-4 h-0 border-gray-200 group-data-[state=error]:border-red-200 group-data-[state=warn]:border-orange-200',
                className,
            )}
            {...props}
        />
    );
}

interface GeneralBlockFooterProps extends React.HTMLAttributes<HTMLDivElement> {}
function GeneralBlockFooter({ className, children }: GeneralBlockFooterProps) {
    return <div className={cn('flex justify-between gap-4', className)}>{children}</div>;
}

export {
    GeneralBlock,
    GeneralBlockHeader,
    GeneralBlockHead,
    GeneralBlockTitle,
    GeneralBlockSubtitle,
    GeneralBlockValue,
    GeneralBlockBody,
    GeneralBlockLabel,
    GeneralBlockSeparator,
    GeneralBlockFooter,
};
